import { Locales } from 'shared/assets/localization';
import { NLP } from 'shared/constants';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { ID } from 'shared/types/id';
import { getDisplayDate } from 'shared/utils/nlp/get-display-date';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { getWordIndexFromMatch } from 'shared/utils/nlp/get-word-index-for-match';

export type Options = {
  nlpRegex: RegExp;
  ignoredWordIds: ID[];
  wordIds: WordId[];
  language: Locales;
  dateFormat: DateFormatOptions;
  createHabitAllowed: boolean;
  scheduleTargetAllowed: boolean;
};

export const removeNlpPropertiesFromString = (
  nlpString: string,
  {
    nlpRegex,
    wordIds,
    ignoredWordIds,
    language,
    dateFormat,
    createHabitAllowed,
    scheduleTargetAllowed,
  }: Options,
) => {
  const isIgnored = (match: RegExpMatchArray) => {
    const wordIndex = getWordIndexFromMatch(match, wordIds);
    const wordId = wordIds[wordIndex]?.id;
    return wordId ? ignoredWordIds.includes(wordId) : false;
  };

  let sentence = nlpString.replace(
    new RegExp(`${NLP.pillSeparator}|${NLP.whitespace}`, 'g'),
    ' ',
  );

  // Collect all matches from the original string first
  const matches = [...nlpString.matchAll(nlpRegex)];

  // get all last matches of each group
  const activeMatches = Object.values(
    matches
      .reverse()
      .reduce<Record<string, RegExpMatchArray[]>>((acc, match) => {
        if (isIgnored(match)) {
          return acc;
        }

        for (const groupName in match.groups ?? {}) {
          // single time and timeRange have effect on each other, so only the last match of either of them has to be taken
          if (match.groups?.[groupName]) {
            if (groupName === 'singleTime') {
              if (!acc.timeRange) {
                acc[groupName] = [match];
              }
              continue;
            }
            if (groupName === 'timeRange') {
              if (!acc.singleTime) {
                acc[groupName] = [match];
              }
              continue;
            }

            // deadlines have to be valid dates
            if (groupName === 'deadline') {
              if (
                !acc[groupName] &&
                getDisplayDate(
                  // take of the `{` and `}` from the match, as it is not part of the date but of the deadline group
                  match[0].replace(/^{|}$/g, ''),
                  language,
                  dateFormat,
                  true,
                )
              ) {
                acc[groupName] = [match];
              }
            }

            // due dates have to be valid dates
            if (groupName === 'dueDate') {
              if (
                !acc[groupName] &&
                getDisplayDate(match[0], language, dateFormat, false)
              ) {
                acc[groupName] = [match];
              }
              continue;
            }

            // only remove schedules
            if (
              groupName === 'target' &&
              (!createHabitAllowed || !scheduleTargetAllowed)
            ) {
              continue;
            }

            if (groupName === 'rrule' && !createHabitAllowed) {
              continue;
            }

            // tags are allowed with more, so we need to have an array of them
            if (groupName === 'tag') {
              acc[groupName] = [
                ...((acc[groupName] ?? []) as RegExpMatchArray[]),
                match as RegExpMatchArray,
              ];
              continue;
            }

            acc[groupName] = acc[groupName] ?? [match];
          }
        }

        return acc;
      }, {}),
  );

  for (const matches of activeMatches) {
    for (const match of matches) {
      const start = match.index!;
      // Also remove any whitespace immediately preceding the match
      let removeStart = start;
      // eslint-disable-next-line no-restricted-syntax -- NLP whitespace trim; disabled per team guidance.
      while (removeStart > 0 && /\s/.test(sentence.charAt(removeStart - 1))) {
        removeStart -= 1;
      }
      const end = start + match[0].length;
      sentence = `${sentence.slice(0, removeStart)}${sentence.slice(end)}`;
    }
  }

  return sentence.trim().replace(/[\s\u00A0\u200B]+/g, ' ');
};
